'use client';

import { Rabbit, Scale, Gem } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SettingsCard, PickerRow } from '@/shared/ui';
import type { RenderEffort } from '@/types/render';
import { useRenderTokenPrices } from '../../hooks/use-render-token-prices';
import { ProBadge } from '@/features/subscription';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';

export interface EffortContentProps {
  selectedEffort: RenderEffort;
  onEffortChange: (effort: RenderEffort) => void;
  // Layout variant — kept for API parity; both variants share the Apple-grouped
  // row idiom.
  variant?: 'default' | 'compact';
  /** Token-mode building (non-pack): show the per-tier "~N tokens" cost hint.
   *  A pack building is unlimited (crown, no token numerics), so it's hidden.
   *  Per-project (multi-product return) — replaces the old account-level
   *  `tokens.is_legacy` gate. Mirrors Apple EffortPickerView.showsTokenNumerics. */
  showTokenCost?: boolean;
  /** Advanced controls locked (no `has_advanced_render_controls`): the HIGH
   *  effort tier is a Pro Pass upsell (PRO badge + paywall on tap);
   *  Standard/Brainstorm stay selectable. Mirrors Apple/Android. */
  locked?: boolean;
  closeOnSelect?: () => void;
}

export function EffortContent({
  selectedEffort,
  onEffortChange,
  showTokenCost = false,
  locked = false,
  closeOnSelect,
}: EffortContentProps) {
  const t = useTranslations('render.settings.effort');
  // Token prices are server-owned and demand-tuned; never use a local constant.
  const { data: tokenPrices } = useRenderTokenPrices();
  const openPassPaywall = usePassPaywallStore((s) => s.open);
  const showLimitPaywall = useLimitPaywallStore((s) => s.showPaywall);
  // Server-driven "unlock this Pro feature" chooser (Pro Pass / Facade Kit /
  // Pro Plan tiles) — the shared advanced-controls payload, same one the Hi-Res
  // / model / output-style locks use. HIGH effort gates on the same
  // is_advanced_render_controls flag, so it shares the code.
  const proFeaturePaywall = usePaywallPayload('PRO_FEATURE_UNAVAILABLE');

  const openLockedPaywall = () => {
    if (proFeaturePaywall) {
      showLimitPaywall(proFeaturePaywall, 'PRO_FEATURE_UNAVAILABLE');
    } else {
      // Fallback only if the cached plan payload hasn't loaded yet.
      openPassPaywall('pro');
    }
  };

  const efforts: Array<{
    type: RenderEffort;
    icon: typeof Scale;
  }> = [
    { type: 'BRAINSTORM', icon: Rabbit },
    { type: 'STANDARD', icon: Scale },
    { type: 'HIGH', icon: Gem },
  ];

  const handleSelect = (effort: RenderEffort) => {
    onEffortChange(effort);
    closeOnSelect?.();
  };

  return (
    <div className="space-y-4">
      {/* Apple parity: grouped card with leading icon + title + description +
          "~N tokens" hint + trailing checkmark. */}
      <SettingsCard>
        {efforts.map((effort) => {
          const isSelected = selectedEffort === effort.type;
          const Icon = effort.icon;
          // Only the HIGH tier is an advanced control — locked to the Pro
          // feature chooser when the building lacks advanced render controls.
          const isLocked = locked && effort.type === 'HIGH';

          const leading = (
            <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
          );

          // Hide the "~N tokens" suffix entirely until the server price loads —
          // no hardcoded fallback, a stale number would misrepresent cost — and
          // only on a token-mode (non-pack) building, where renders draw from
          // the visible wallet.
          const tokenPrice = tokenPrices?.[effort.type];
          const baseDescription = t(`types.${effort.type}.description`);
          const description =
            tokenPrice === undefined || !showTokenCost
              ? baseDescription
              : `${baseDescription} · ${t('tokenHint', { count: tokenPrice })}`;

          return (
            <PickerRow
              key={effort.type}
              title={t(`types.${effort.type}.name`)}
              description={description}
              leading={leading}
              trailing={isLocked ? <ProBadge /> : undefined}
              isSelected={!isLocked && isSelected}
              onClick={
                isLocked ? openLockedPaywall : () => handleSelect(effort.type)
              }
            />
          );
        })}
      </SettingsCard>
    </div>
  );
}

// Helper to get subtitle for preview
export function getEffortSubtitle(
  selectedEffort: RenderEffort,
  t: (key: string) => string
): string {
  return t(`types.${selectedEffort}.name`);
}
